// admin/socket/reducerBridge.ts — inbound socket event → Redux dispatch glue.
//
// Maps every AdminEvent variant onto the right slice action(s). Called from
// AdminApp's effect when the socket emits 'event'. Keeps the wire shape and
// the Redux mutations decoupled — slices are dumb stores; this file is the
// adapter.

import type { AdminEvent, AdminStoryIdea } from "../store/types";
import type { AppDispatch, RootState } from "../store";
import {
  dashboardActions,
  eventsActions,
  ideasActions,
  sfxActions,
  store,
  usersActions,
} from "../store";

/**
 * Route an inbound AdminEvent to the appropriate slice actions.
 *
 * Every event lands in the events ring buffer (for the live activity panel).
 * dashboard_refresh additionally overwrites the dashboard slice.
 * flag_toggled additionally patches the matching row in the users page when
 * it's in view.
 */
export function routeEvent(evt: AdminEvent, dispatch: AppDispatch): void {
  // Always push to the activity ring buffer (newest first, capped at 100).
  // Suppress dashboard_refresh — it's a per-tick aggregate, not an "event"
  // that's meaningful to surface in the activity feed. Counters move on
  // their own from the dashboard slice update.
  if (evt.type !== "dashboard_refresh") {
    dispatch(eventsActions.push(evt));
  }

  switch (evt.type) {
    case "dashboard_refresh":
      dispatch(dashboardActions.set(evt.payload));
      break;
    case "flag_toggled":
      // We don't know the full AdminUser here — the toggleFlag RPC ack carried
      // it and was applied via usersActions.patchRow at the call site.
      // Still useful for activity feed (already pushed above).
      break;
    case "story_idea_submitted": {
      // Live-prepend the new row to the Ideas page ONLY when the current
      // view is on the default filters/sort/page (status=all + sort=created_at
      // desc + page=1). Other views just let the dashboard counter bump.
      //
      // The event payload only carries bodyPreview (200 chars), not the
      // full body. The prepended row stores the preview in the `body` field
      // and accepts that expanding a live-added row shows only the preview;
      // a fresh page query rehydrates the full body. Documented as
      // acceptable for v1.
      const state: RootState = store.getState();
      const q = state.ideas.query;
      const isDefaultView =
        q.status === "all" &&
        q.sort === "created_at" &&
        q.order === "desc" &&
        q.page === 1 &&
        q.q.trim() === "";
      if (isDefaultView) {
        const row: AdminStoryIdea = {
          id: evt.payload.id,
          userId: evt.payload.userId,
          userEmail: evt.payload.userEmail,
          userName: evt.payload.userName,
          body: evt.payload.bodyPreview,
          status: "new",
          adminNotes: null,
          createdAt: evt.payload.createdAt,
        };
        dispatch(ideasActions.prependFresh(row));
      }
      break;
    }
    case "sfx_tag_emitted":
      // Apply the live count bump into the SFX slice so the admin page
      // updates without a refetch. If the page hasn't loaded the initial
      // list yet, the slice still appends the row — the next full query
      // result (sfxStatsLoaded) overwrites this with the authoritative
      // snapshot.
      dispatch(sfxActions.sfxTagEmitted(evt.payload));
      break;
    case "user_signed_up":
    case "turn_consumed":
    case "turn_granted":
    case "subscription_changed":
    case "story_created":
    case "story_deleted":
    case "story_restarted":
    case "credit_exhausted":
    case "referral_bonus_deposited":
    case "referral_flag_changed":
    case "account_deletion_scheduled":
    case "account_deletion_cancelled":
    case "account_deletion_finalized":
      // No per-event slice mutation beyond the activity feed. The trailing
      // dashboard_refresh handles aggregate counter updates. The admin
      // Referrals / AccountDeletions pages run their own RPC queries on
      // mount and re-fetch on the relevant broadcasts via Redux selector
      // subscriptions in the page components.
      break;
    default: {
      // Exhaustiveness check — TS will error if a new AdminEvent variant
      // isn't handled above.
      const _exhaustive: never = evt;
      void _exhaustive;
      break;
    }
  }

  // Avoid unused-import lint for usersActions when no branch hits patchRow.
  // (Slice patch from the toggleFlag ack is done in pages/Users.tsx with
  // the AdminUser payload.)
  void usersActions;
}
